import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Add,
  Edit,
  LockOpen,
  Lock,
  LockReset,
  Search,
} from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { isAxiosError } from 'axios';
import {
  useUsuarios,
  useDesactivarUsuario,
  useActivarUsuario,
  useResetPasswordUsuario,
} from '../../hooks/useUsuarios';
import { useAuth } from '../../context/AuthContext';
import UsuarioDialog from './UsuarioDialog';
import type { Usuario, UserRole } from '../../types/usuario';
import { getRolLabel } from '../../types/usuario';
import { formatDate, formatCuit } from '../../utils/format';

/**
 * Extrae un mensaje legible de un error de la query.
 * Diferencia entre errores de axios (con response) y errores genéricos.
 */
function formatErrorMessage(err: unknown): { titulo: string; detalle: string; tip?: string } {
  if (isAxiosError(err)) {
    const status = err.response?.status;
    const body = err.response?.data as { error?: string } | undefined;
    const serverMsg = body?.error;

    if (status === 401) {
      return {
        titulo: 'Sin autorización (401)',
        detalle:
          serverMsg ??
          'El servidor rechazó la solicitud por falta de autenticación. ' +
            'Tu sesión podría haber expirado.',
        tip: 'Probá cerrar sesión y volver a entrar.',
      };
    }
    if (status === 403) {
      return {
        titulo: 'Acceso denegado (403)',
        detalle: serverMsg ?? 'Tu rol no tiene permiso para ver esta sección.',
        tip: 'Esta pantalla requiere rol "admin_general".',
      };
    }
    if (status === 404) {
      return {
        titulo: 'No encontrado (404)',
        detalle: serverMsg ?? 'El endpoint no existe en el servidor.',
      };
    }
    if (status && status >= 500) {
      return {
        titulo: `Error del servidor (${status})`,
        detalle: serverMsg ?? 'El servidor tuvo un error interno al procesar la solicitud.',
        tip: 'Avisá al equipo de desarrollo con el mensaje exacto.',
      };
    }
    if (!err.response) {
      // No hay response → es error de red (backend caído, sin conexión, CORS)
      return {
        titulo: 'No se pudo conectar al servidor',
        detalle:
          'No llegó respuesta del backend. El servidor podría no estar ' +
          'corriendo, o hay un problema de red.',
        tip: 'Verificá que el backend esté arriba (puerto 3001).',
      };
    }
    return {
      titulo: `Error HTTP ${status ?? '?'}`,
      detalle: serverMsg ?? err.message,
    };
  }
  return {
    titulo: 'Error al cargar usuarios',
    detalle: (err as Error)?.message ?? 'Error desconocido',
  };
}

// ── Helpers de presentación ──────────────────────────────────────────────────

type ChipColor = 'primary' | 'warning' | 'success' | 'default';

function rolColor(rol: UserRole): ChipColor {
  switch (rol) {
    case 'admin_general': return 'primary';
    case 'operador':      return 'warning';
    case 'admin_empresa': return 'success';
    default:              return 'default';
  }
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function UsuariosList() {
  const { profile } = useAuth();
  const {
    data: usuarios = [],
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useUsuarios();
  const desactivar = useDesactivarUsuario();
  const activar    = useActivarUsuario();
  const resetPwd   = useResetPasswordUsuario();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Usuario | null>(null);
  const [search, setSearch]         = useState('');

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit   = (u: Usuario) => { setEditing(u); setDialogOpen(true); };

  // Filtro client-side por nombre, apellido o email
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return usuarios;
    return usuarios.filter((u) =>
      `${u.nombre} ${u.apellido ?? ''} ${u.email} ${u.cuit ?? ''}`.toLowerCase().includes(term)
    );
  }, [usuarios, search]);

  const handleDesactivar = async (u: Usuario) => {
    if (!confirm(`¿Desactivar al usuario ${u.nombre} ${u.apellido ?? ''}? No podrá iniciar sesión.`)) return;
    await desactivar.mutateAsync(u.id);
  };

  const handleActivar = async (u: Usuario) => {
    if (!confirm(`¿Activar al usuario ${u.nombre} ${u.apellido ?? ''}?`)) return;
    await activar.mutateAsync(u.id);
  };

  const handleResetPwd = async (u: Usuario) => {
    if (!confirm(`¿Enviar email de recuperación de contraseña a ${u.email}?`)) return;
    const result = await resetPwd.mutateAsync(u.id);
    alert(result.message);
  };

  const columns = useMemo<GridColDef<Usuario>[]>(
    () => [
      {
        field: 'apellido',
        headerName: 'Nombre completo',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams<Usuario>) => (
          <Typography variant="body2" fontWeight={500}>
            {[p.row.apellido, p.row.nombre].filter(Boolean).join(', ')}
          </Typography>
        ),
      },
      {
        field: 'cuit',
        headerName: 'CUIT',
        width: 140,
        renderCell: (p: GridRenderCellParams<Usuario>) => (
          <Typography variant="body2" fontFamily="monospace">
            {p.row.cuit ? formatCuit(p.row.cuit) : '—'}
          </Typography>
        ),
      },
      {
        field: 'email',
        headerName: 'Correo',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'rol',
        headerName: 'Rol',
        width: 150,
        renderCell: (p: GridRenderCellParams<Usuario>) => (
          <Chip
            label={getRolLabel(p.value as UserRole)}
            size="small"
            color={rolColor(p.value as UserRole)}
          />
        ),
      },
      {
        field: 'empresa',
        headerName: 'Empresa',
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (p: GridRenderCellParams<Usuario>) =>
          p.row.rol === 'admin_empresa'
            ? (p.row.empresa?.razon_social ?? '—')
            : <Typography variant="body2" color="text.disabled">—</Typography>,
      },
      {
        field: 'activo',
        headerName: 'Estado',
        width: 110,
        renderCell: (p: GridRenderCellParams<Usuario>) => (
          <Chip
            label={p.value ? 'Activo' : 'Inactivo'}
            size="small"
            color={p.value ? 'success' : 'default'}
            variant={p.value ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Alta',
        width: 110,
        renderCell: (p) => formatDate(p.value as string),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Usuario>) => {
          const u = p.row;
          const esMiUsuario = u.id === profile?.id;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => openEdit(u)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>

              {u.activo ? (
                <Tooltip title={esMiUsuario ? 'No podés desactivar tu propio usuario' : 'Desactivar'}>
                  <span>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleDesactivar(u)}
                      disabled={esMiUsuario}
                    >
                      <Lock fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Activar">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleActivar(u)}
                  >
                    <LockOpen fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Resetear contraseña">
                <IconButton size="small" onClick={() => handleResetPwd(u)}>
                  <LockReset fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [profile?.id, desactivar.isPending, activar.isPending, resetPwd.isPending]
  );

  return (
    <Box>
      {/* Encabezado */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Usuarios del sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestión de cuentas, roles y accesos
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nuevo usuario
        </Button>
      </Stack>

      {/* Buscador */}
      <TextField
        size="small"
        placeholder="Buscar por nombre, apellido, CUIT o correo…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
      />

      {isError && (() => {
        const e = formatErrorMessage(error);
        return (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isRefetching}
              >
                {isRefetching ? 'Reintentando…' : 'Reintentar'}
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600}>
              {e.titulo}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {e.detalle}
            </Typography>
            {e.tip && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                💡 {e.tip}
              </Typography>
            )}
          </Alert>
        );
      })()}

      {/* Tabla */}
      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          disableVirtualization
          autoHeight
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default' },
            '& .MuiDataGrid-row--inactive': { opacity: 0.55 },
          }}
          getRowClassName={(p) => (!p.row.activo ? 'MuiDataGrid-row--inactive' : '')}
          localeText={{
            noRowsLabel: 'No hay usuarios cargados',
            footerTotalRows: 'Total:',
            MuiTablePagination: {
              labelRowsPerPage: 'Por página:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
            },
          }}
        />
      </Card>

      <UsuarioDialog
        open={dialogOpen}
        usuario={editing}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
